/**
 * Implements a wallet overview table and user actions
 */
import { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { useAccount } from 'wagmi';
import {
  IWalletOverview,
  IWalletTradesCountAndVolume,
  Network,
} from '@kattana-io/common';
import { authTokenSelector } from '../../../selectors/auth';
import { UserAPI } from '../../../store/createStore';
import { actionShowModal } from '../../../actions/modal';
import { ModalTypes } from '../../../reducers/modal';
import { onCopyAddress, onWalletScan } from '../../../helpers/addressUtils';
import Text from '../../../atoms/Text';
import Badge from '../../../molecules/Badge';
import WalletWatchlistStar from './WalletWatchlistStar';
import { formatWallet } from '../../../components/Modal/WalletModal/formatters';
import NumberDisplay from '../../../molecules/NumberDisplay';
import { createGetFromWalletTags } from '../../../selectors/walletTags';
import AccountOverviewPlaceholder from './Placeholder';

const ACTIVE_WALLET_TOOLTIP = 'Traded in the last 24 hours';
const INACTIVE_WALLET_TOOLTIP = 'No trades in the last 24 hours';

enum WalletStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const getOverviewColumns = (
  wallet: IWalletOverview | null,
  tradesAndVolume: IWalletTradesCountAndVolume | null
): { title: string; value: number; round?: boolean }[] => {
  return [
    {
      title: 'Total actions:',
      value: tradesAndVolume?.trades_count_total || 0,
    },
    {
      title: '24h trading volume:',
      value: wallet?.trades_volume_24_h || 0,
      round: true,
    },
    {
      title: 'Total trading volume:',
      value: tradesAndVolume?.trades_value_total || 0,
      round: true,
    },
    { title: 'Total tokens:', value: wallet?.tokens_count || 0 },
    { title: 'Total NFTs:', value: wallet?.nft_count || 0 },
  ];
};

interface IWalletProps {
  wallet: IWalletOverview | null;
  tradesAndVolume: IWalletTradesCountAndVolume | null;
  chain?: string;
  address?: string;
  loading: boolean;
}

const Wallet = ({
  wallet,
  tradesAndVolume,
  chain,
  address,
  loading,
}: IWalletProps) => {
  const selectFromWalletTags = useMemo(
    () => createGetFromWalletTags(wallet?.wallet_address, chain),
    [wallet?.wallet_address, chain]
  );

  const currentTag = useSelector(selectFromWalletTags);
  const authToken = useSelector(authTokenSelector);

  const editRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const { address: ownAddress } = useAccount();

  const network = Network.fromChain(chain || 'ETH');
  const walletAddress = wallet?.wallet_address || address;

  const onEdit = async () => {
    const internalNetwork = network.getSymbol();
    let networkType = 'EVM';
    if (internalNetwork == 'ELROND') {
      networkType = 'ELROND';
    } else if (internalNetwork == 'TRON') {
      networkType = 'TRON';
    } else if (internalNetwork == 'TON') {
      networkType = 'TON';
    }

    const user = await UserAPI.fetchUser(ownAddress!, networkType);

    if (user) {
      dispatch(
        actionShowModal(ModalTypes.editProfile, {
          user,
          networkType,
          authToken,
        })
      );
    }
  };

  const onCopyWallet = () => {
    if (walletAddress) {
      onCopyAddress(walletAddress);
    }
  };

  const onScan = () => {
    if (walletAddress && chain) {
      onWalletScan(walletAddress, chain);
    }
  };

  const onWalletTagEdit = () => {
    const rect = editRef.current?.getBoundingClientRect();
    if (rect && walletAddress && chain) {
      dispatch(
        actionShowModal(ModalTypes.editWalletTag, {
          wallet: walletAddress,
          network: chain,
          currentTag,
          x: rect.x - 35,
          y: rect.y + 35,
        })
      );
    }
  };

  const status = wallet?.trades_volume_24_h
    ? WalletStatus.ACTIVE
    : WalletStatus.INACTIVE;
  const overviewTableData = getOverviewColumns(wallet, tradesAndVolume);
  const editProfileCheck =
    ownAddress && ownAddress === walletAddress && authToken;
  const nickname = currentTag || wallet?.nickname;
  const name = nickname
    ? `${nickname} (${formatWallet(walletAddress || '')})`
    : walletAddress;

  return (
    <>
      <div className='overview__active_wallet'>
        <div className='overview__active_wallet__info'>
          <div className='overview__active_wallet__info__description'>
            <Text size={14} className='text--navy-600'>
              {name}
            </Text>
            <Badge
              variant='fill'
              klass={status === WalletStatus.ACTIVE ? 'green' : 'orange'}
              text={status}
              size={10}
              weight={600}
              padding='2px 6px'
              borderRadius={40}
              tooltip={
                status === WalletStatus.ACTIVE
                  ? ACTIVE_WALLET_TOOLTIP
                  : INACTIVE_WALLET_TOOLTIP
              }
            />
          </div>
        </div>

        <div className='overview__active_wallet__actions'>
          {wallet && (
            <WalletWatchlistStar
              data={{ type: 'overview', walletId: wallet.wallet_id }}
            />
          )}
          <div
            className='overview__active_wallet__actions__item icon-edit'
            ref={editRef}
            onClick={editProfileCheck ? onEdit : onWalletTagEdit}
          />
          {walletAddress && (
            <div
              className='overview__active_wallet__actions__item icon-copy'
              onClick={onCopyWallet}
            />
          )}
          {walletAddress && chain && (
            <div
              className='overview__active_wallet__actions__item icon-top-right-square'
              onClick={onScan}
            />
          )}
        </div>
      </div>
      <div className='overview__body'>
        <div className='overview__body__title'>
          <div className='overview__body__title__label'>
            <Text size={16} className='text--grey-200'>
              Account overview
            </Text>
            <div
              className='overview__body__title__label__icon icon-alert-circle'
              data-for='account-overview-tooltip'
              data-tip="Check selected crypto wallet's<br>overview for a snapshot of its<br>holdings, transactions, and balance."
            />
            <ReactTooltip
              id='account-overview-tooltip'
              type='light'
              place='bottom'
              effect='solid'
              className='text_tooltip'
              multiline={true}
            />
          </div>
        </div>
        <div className='overview__body__separator' />
        {loading ? (
          <AccountOverviewPlaceholder />
        ) : (
          <div className='overview__body__table'>
            {overviewTableData.map((column) => (
              <div key={column.title} className='overview__body__table__column'>
                <Text size={12}>{column.title}</Text>
                {column.round ? (
                  <NumberDisplay
                    value={column.value}
                    textProps={{ size: 16 }}
                  />
                ) : (
                  <Text size={16} style={{ title: column.value.toString() }}>
                    {column.value}
                  </Text>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default Wallet;
